import * as React from 'react';
import { useMutation, useQuery } from '@apollo/client';

import { FaSave } from 'react-icons/fa';
import { isValidEmailInput } from '@/utils/email';
import { camelToSnakeCase } from '@/utils/formatter';
import i18n from '@/utils/i18n';
import { IconButton } from '../Button/Button';
import { NotChangeableMerchantOrderStatus } from './HandleStatusChange';
import {
  checkDeliveryAddress,
  deliveryAddressToDpdAddress,
  getDeliveryAddress,
} from './Utils';
import { isValidPostalCode, normalizePostalCode } from '@/utils/postal';
import { updateStore } from '../../graphql/mutations';
import { allUserCountriesQuery } from '../../graphql/queries';
import { ContentSection } from '../Layout/ContentSection';

const allowedAddressFields = [
  'company',
  'first_name',
  'last_name',
  'streetname',
  'streetno',
  'zip',
  'city',
  'state',
  'additional_info',
  'email',
  'phone_number',
  'country',
];

const usStates = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
];

const canadaProvinces = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

/**
 * Only to be used in EditMerchantOrder
 */
export const DeliveryAddressEditItem = (props: {
  handleChange: any;
  ogRevisionStatus: number;
  storeUpdate: any;
  orderExecutionDate: Date;
  resetStore: any;
  canAccessPartnerData: boolean;
  orderType: number;
}) => {
  const {
    handleChange,
    ogRevisionStatus,
    storeUpdate,
    orderExecutionDate,
    resetStore,
    canAccessPartnerData,
    orderType,
  } = props;
  const [updateStoreMutation, { loading: updating }] = useMutation(updateStore);
  const { data: countriesData = { __type: { enumValues: [] } } } = useQuery(
    allUserCountriesQuery,
  );
  const countries = countriesData.__type.enumValues.map(
    (country: any) => country.name,
  );

  async function handleSave() {
    if (!window.confirm(i18n.t('MerchantOrder:confirmSave'))) {
      return;
    }

    const address: any = getDeliveryAddress(storeUpdate.delivery_address);

    const success = checkDeliveryAddress(
      deliveryAddressToDpdAddress(address),
      storeUpdate.id,
      orderExecutionDate,
    );
    if (!success) {
      return;
    }

    const getMutationInput = (storeIn: any) => {
      for (const key of Object.keys(address || {})) {
        storeIn.delivery_address[camelToSnakeCase(key)] = address[key];
      }
      storeIn.delivery_address = Object.fromEntries(
        Object.entries(storeIn.delivery_address || {})
          .filter(([key]) => allowedAddressFields.includes(key))
          .map(([key, value]) => [
            key,
            value != null ? (value as string).trim() : value,
          ]),
      );
      const mutationInput = {
        delivery_address: {
          ...storeIn.delivery_address,
          __typename: undefined,
        },
      };
      return mutationInput;
    };

    const mutationInput = getMutationInput(storeUpdate);
    console.log('mutationInput', mutationInput);
    updateStoreMutation({
      variables: {
        id: storeUpdate.id,
        store: mutationInput,
      },
      update: (cache, { data: { updateStore: updatedStore } }) => {
        resetStore(updatedStore);
      },
    }).catch((err: any) => {
      alert('Error updating store: ' + err);
    });
  }

  function fieldsDisabled() {
    return NotChangeableMerchantOrderStatus.includes(ogRevisionStatus);
  }

  const isUSA = (storeUpdate.delivery_address || {}).country === 'US';
  const isCanada = (storeUpdate.delivery_address || {}).country === 'CA';

  const stateOptions = isUSA ? usStates : isCanada ? canadaProvinces : [];

  return (
    <ContentSection title={i18n.t('MerchantOrder:deliveryAddress')}>
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('MerchantOrder:company')}
        </span>
        <input
          type="text"
          className="form-control"
          name="delivery_address.company"
          onChange={handleChange}
          disabled={fieldsDisabled()}
          value={(storeUpdate.delivery_address || {}).company || ''}
        />
      </div>
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('MerchantOrder:firstName')}
        </span>
        <input
          type="text"
          className="form-control"
          name="delivery_address.first_name"
          onChange={handleChange}
          disabled={fieldsDisabled()}
          value={(storeUpdate.delivery_address || {}).first_name || ''}
        />
      </div>
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('MerchantOrder:lastName')}
        </span>
        <input
          type="text"
          className="form-control"
          name="delivery_address.last_name"
          onChange={handleChange}
          disabled={fieldsDisabled()}
          value={(storeUpdate.delivery_address || {}).last_name || ''}
        />
      </div>
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('Common:street')}
        </span>
        <input
          type="text"
          className="form-control"
          name="delivery_address.streetname"
          onChange={handleChange}
          disabled={fieldsDisabled()}
          value={(storeUpdate.delivery_address || {}).streetname || ''}
        />
      </div>
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('Common:streetNumber')}
        </span>
        <input
          type="text"
          className="form-control"
          onChange={handleChange}
          name="delivery_address.streetno"
          disabled={fieldsDisabled()}
          value={(storeUpdate.delivery_address || {}).streetno || ''}
        />
      </div>
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('Common:zipCode')}
        </span>
        <input
          type="text"
          maxLength={10}
          className={
            'form-control ' +
            (!isValidPostalCode(
              (storeUpdate.delivery_address || {}).zip,
              (storeUpdate.delivery_address || {}).country,
            )
              ? 'is-invalid'
              : '')
          }
          name="delivery_address.zip"
          onChange={(e) => {
            const normalized = normalizePostalCode(
              e.target.value.substring(0, 10),
              (storeUpdate.delivery_address || {}).country,
            );
            handleChange({
              target: { name: 'delivery_address.zip', value: normalized },
            });
          }}
          disabled={fieldsDisabled()}
          value={(storeUpdate.delivery_address || {}).zip || ''}
        />
      </div>
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('Common:city')}
        </span>
        <input
          type="text"
          className="form-control"
          name="delivery_address.city"
          onChange={handleChange}
          value={(storeUpdate.delivery_address || {}).city || ''}
          disabled={fieldsDisabled()}
          placeholder={i18n.t('GeneralAdminInfo:cityNameExample')}
        />
      </div>
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('Common:country')}
        </span>
        <select
          className="form-control custom-select"
          name="delivery_address.country"
          onChange={handleChange}
          disabled={fieldsDisabled()}
          value={(storeUpdate.delivery_address || {}).country}
        >
          {countries.length === 0
            ? null
            : countries.map((c: string) => (
                <option key={c} value={c}>
                  {i18n.t(`Common:Country:${c}`) as string}
                </option>
              ))}
        </select>
      </div>
      {(isUSA || isCanada) && (
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('Common:state')}
          </span>
          <select
            className="form-control custom-select"
            name="delivery_address.state"
            onChange={handleChange}
            value={(storeUpdate.delivery_address || {})?.state || ''}
            disabled={fieldsDisabled()}
          >
            <option value="">{i18n.t('Common:selectState')}</option>
            {stateOptions.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('MerchantOrder:email')}
        </span>
        <input
          type="text"
          className={
            'form-control ' +
            (!isValidEmailInput((storeUpdate.delivery_address || {}).email)
              ? 'is-invalid'
              : '')
          }
          name="delivery_address.email"
          autoComplete="off"
          lang="en-US"
          onChange={(e) => {
            handleChange(e);
          }}
          value={(storeUpdate.delivery_address || {}).email || ''}
          disabled={fieldsDisabled()}
          placeholder={'bob@example.com;alice@example.com'}
        />
      </div>
      <div className="input-group mb-3">
        <span className="input-group-text" id="basic-addon1">
          {i18n.t('MerchantOrder:phone')}
        </span>
        <input
          type="text"
          className={'form-control'}
          name="delivery_address.phone_number"
          autoComplete="off"
          onChange={(e) => {
            handleChange(e);
          }}
          value={(storeUpdate.delivery_address || {}).phone_number || ''}
          disabled={fieldsDisabled()}
          placeholder={'123456789'}
        />
      </div>
      {canAccessPartnerData && (
        <div className="col-sm">
          <IconButton
            icon={<FaSave />}
            label={i18n.t('MerchantOrder:saveDeliveryAddressAsDefault')}
            onClick={handleSave}
            disabled={fieldsDisabled()}
            isLoading={updating}
            style={{ float: 'right' }}
          />
        </div>
      )}
    </ContentSection>
  );
};
